import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth }  from '@/auth/AuthContext'
import { useStore } from '@/store/StoreContext'
import { useT }     from '@/i18n/I18nContext'
import { AlgorithmAnalysisSection, LearningModulesSection } from './AlgorithmPanels';
import { DISABILITIES } from '@/constants/ncfFramework';
const API = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';


const DOMAIN_IDS    = ['physical-motor','socio-emotional','cognitive','language-literacy','numeracy'];
const DOMAIN_LABELS = {
  'physical-motor':    'Physical & Motor',
  'socio-emotional':   'Socio-Emotional',
  'cognitive':         'Cognitive',
  'language-literacy': 'Language & Literacy',
  'numeracy':          'Early Numeracy',
};
const DOMAIN_COLORS = {
  'physical-motor':    '#1B6B45',
  'socio-emotional':   '#C0392B',
  'cognitive':         '#6B2D8B',
  'language-literacy': '#1A4A6E',
  'numeracy':          '#D4AC0D',
};
const DOMAIN_ICONS = {
  'physical-motor':    '🏃',
  'socio-emotional':   '💛',
  'cognitive':         '🧠',
  'language-literacy': '📚',
  'numeracy':          '🔢',
};

const LEVEL_SCORE = { 'not-observed': 0, 'N/O': 0, emerging: 33, 'E': 33, developing: 66, 'D': 66, achieved: 100, 'A': 100 };

const PALETTE = ['#1A4A6E','#C0392B','#1B6B45','#6B2D8B','#D4AC0D','#0D5C63','#8B3A2A','#2E4057'];

function calcAge(dob) {
  if (!dob) return null;
  const ms = Date.now() - new Date(dob).getTime();
  const y  = Math.floor(ms / (365.25 * 24 * 3600000));
  const m  = Math.floor((ms % (365.25 * 24 * 3600000)) / (30.44 * 24 * 3600000));
  return y > 0 ? `${y}y ${m}m` : `${m}m`;
}

function initials(name = '') {
  return (name.trim().split(/\s+/).map(w => w[0]||'').join('').slice(0,2)||'??').toUpperCase();
}

function domainScores(indicators) {
  if (!indicators || !Array.isArray(indicators) || indicators.length === 0) return {};
  const scores = {};
  for (const domainId of DOMAIN_IDS) {
    const domainInds = indicators.filter(i => i.domainId === domainId);
    if (domainInds.length === 0) { scores[domainId] = null; continue; }
    const total = domainInds.reduce((sum, i) => {
      const lvl = i.current?.level || 'not-observed';
      return sum + (LEVEL_SCORE[lvl] ?? 0);
    }, 0);
    scores[domainId] = Math.round(total / domainInds.length);
  }
  return scores;
}

function overallScore(scores) {
  const vals = Object.values(scores).filter(v => v !== null);
  if (!vals.length) return null;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

function scoreColor(pct) {
  if (pct === null) return '#ccc';
  if (pct >= 80) return '#1B6B45';
  if (pct >= 50) return '#D4AC0D';
  return '#C0392B';
}

function Avatar({ name, color, size = 36 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color || '#1A4A6E', color: 'white',
      fontWeight: 900, fontSize: size * 0.36,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      letterSpacing: -0.5,
    }}>
      {initials(name)}
    </div>
  );
}

function sessionAccuracy(s) {
  if (!s) return null;
  if (s.correctCount != null && s.ratedCount > 0) {
    return Math.round((s.correctCount / s.ratedCount) * 100);
  }
  if (s.correctCount != null && s.completionRate > 0 && s.totalCards > 0) {
    const rated = Math.round(s.totalCards * s.completionRate / 100);
    return rated > 0 ? Math.round((s.correctCount / rated) * 100) : null;
  }
  return null;
}

function fmtResponseTime(ms) {
  if (!ms || ms <= 0) return null;
  return `${Math.round(ms / 100) / 10}s`;
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ChildrenView({ onNav }) {
  const t                       = useT();
  const { user, getAuthHeader } = useAuth();
  const { logActivity }         = useStore();

  const [children,  setChildren]  = useState(null);
  const [records,   setRecords]   = useState({});
  const [algData,   setAlgData]   = useState({});
  const [analytics, setAnalytics] = useState({});
  const [sel,       setSel]       = useState(null);
  const [editing,   setEditing]   = useState(null);
  const [showAdd,   setShowAdd]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [toast,     setToast]     = useState('');
  const [search,    setSearch]    = useState('');
  const [filterDis, setFilterDis] = useState('');
  const [viewMode,  setViewMode]  = useState('grid');
  const searchRef = useRef(null);

  const showToast = (msg, ms = 2800) => { setToast(msg); setTimeout(() => setToast(''), ms); };

  // ── SCHOOL-SCOPED fetch ────────────────────────────────────────────────────
  // Always pass ?school= so only children from the user's school are returned.
  // The backend enforces this too, but being explicit prevents any edge cases.
  const load = useCallback(async () => {
    try {
      const schoolQ = user?.school ? `&school=${user.school}` : ''
      const res  = await fetch(`${API}/children?limit=200${schoolQ}`, { headers: getAuthHeader() });
      const data = await res.json();
      if (data.success) {
        setChildren(data.children);
        if (!sel && data.children.length > 0) setSel(data.children[0]._id);
        loadRecords(data.children.map(c => c._id));
      }
    } catch { setChildren([]); }
  }, [getAuthHeader, user?.school]);

  const loadRecords = useCallback(async (childIds) => {
    if (!childIds?.length) return;
    try {
      const results = await Promise.allSettled(
        childIds.map(id =>
          fetch(`${API}/progress/${id}`, { headers: getAuthHeader() })
            .then(r => r.json())
            .then(d => d.record ? { id, record: d.record } : null)
        )
      );
      const map = {};
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value) {
          map[r.value.id] = r.value.record;
        }
      });
      setRecords(map);
    } catch {}
  }, [getAuthHeader]);

  useEffect(() => {
    if (!sel) return;
    if (records[sel] === undefined) {
      (async () => {
        try {
          const res  = await fetch(`${API}/progress/${sel}`, { headers: getAuthHeader() });
          const data = await res.json();
          if (data.success && data.record) {
            setRecords(prev => ({ ...prev, [sel]: data.record }));
          }
        } catch {}
      })();
    }
    if (algData[sel] === undefined) {
      (async () => {
        try {
          const [qRes, sRes] = await Promise.allSettled([
            fetch(`${API}/flashcard-sessions/${sel}/review-queue`, { headers: getAuthHeader() }).then(r=>r.json()),
            fetch(`${API}/flashcard-sessions/${sel}?limit=5`,      { headers: getAuthHeader() }).then(r=>r.json()),
          ]);
          setAlgData(prev => ({
            ...prev,
            [sel]: {
              queue:          qRes.status==='fulfilled' ? qRes.value  : null,
              recentSessions: sRes.status==='fulfilled' ? (sRes.value?.sessions || []) : [],
            }
          }));
        } catch {}
      })();
    }
    if (analytics[sel] === undefined) {
      setAnalytics(prev => ({ ...prev, [sel]: null }));
      (async () => {
        try {
          const [aRes, sRes] = await Promise.allSettled([
            fetch(`${API}/flashcard-sessions/${sel}/analytics`, { headers: getAuthHeader() })
              .then(r => r.ok ? r.json() : null).catch(() => null),
            fetch(`${API}/flashcard-sessions/${sel}?limit=20`, { headers: getAuthHeader() })
              .then(r => r.json()).catch(() => ({ sessions: [] })),
          ]);
          setAnalytics(prev => ({
            ...prev,
            [sel]: {
              summary:  aRes.status==='fulfilled' ? aRes.value  : null,
              sessions: sRes.status==='fulfilled' ? (sRes.value?.sessions || []) : [],
            }
          }));
        } catch {
          setAnalytics(prev => ({ ...prev, [sel]: { summary: null, sessions: [] } }));
        }
      })();
    }
  }, [sel, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const child = (children || []).find(c => c._id === sel);

  const filtered = (children || []).filter(c => {
    const q = search.toLowerCase();
    const matchName = !q || c.name.toLowerCase().includes(q);
    const matchDis  = !filterDis || (c.disabilities || []).some(d => d.toLowerCase().includes(filterDis.toLowerCase()));
    return matchName && matchDis;
  });

  const allDisabilities = [...new Set((children || []).flatMap(c => c.disabilities || []))].sort();

  const handleAdd = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form, classroom: form.classroomId || null };
      delete payload.classroomId;
      const res  = await fetch(`${API}/children`, { method:'POST', headers: getAuthHeader(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!data.success) { showToast('⚠️ ' + (data.message||'Failed')); setSaving(false); return; }

      const newChild = data.child;

      if (form.classroomId) {
        try {
          await fetch(`${API}/classrooms/${form.classroomId}/students`, {
            method: 'POST',
            headers: getAuthHeader(),
            body: JSON.stringify({ add: [newChild._id] }),
          });
        } catch {
          showToast('⚠️ Child created but classroom link failed — contact admin');
        }
      }

      setChildren(prev => [...prev, newChild]);
      setSel(newChild._id);
      logActivity?.({ module:'children', child:newChild.name, action:'New child profile added', icon:'👤' });
      showToast('✅ Child added' + (form.classroomId ? ' & assigned to classroom' : ''));
      setShowAdd(false);
    } catch { showToast('⚠️ Network error'); }
    setSaving(false);
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      const res  = await fetch(`${API}/children/${form._id}`, { method:'PUT', headers: getAuthHeader(), body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) {
        setChildren(prev => prev.map(c => c._id === data.child._id ? data.child : c));
        logActivity?.({ module:'children', child:data.child.name, action:'Profile updated', icon:'👤' });
        showToast('✅ Profile updated');
        setEditing(null);
      } else showToast('⚠️ ' + (data.message||'Update failed'));
    } catch { showToast('⚠️ Network error'); }
    setSaving(false);
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Remove ${c.name}'s profile?`)) return;
    try {
      await fetch(`${API}/children/${c._id}`, { method:'DELETE', headers: getAuthHeader() });
      const next = (children||[]).filter(x => x._id !== c._id);
      setChildren(next);
      setSel(next[0]?._id||null);
      logActivity?.({ module:'children', child:c.name, action:'Profile deleted', icon:'👤' });
      showToast('🗑 Profile removed');
    } catch { showToast('⚠️ Delete failed'); }
  };

  const detailOpen = !!(sel && child);

  if (children === null) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', flexDirection:'column', gap:14 }}>
      <div style={{ width:26, height:26, border:'3px solid var(--sky)', borderTop:'3px solid transparent', borderRadius:'50%', animation:'spin .7s linear infinite' }} />
      <div style={{ fontSize:13, color:'var(--mist)', fontWeight:600 }}>Loading profiles…</div>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 60px)', overflow:'hidden' }}>
      {toast && (
        <div style={{ position:'fixed', bottom:24, right:24, background:'#1a1a2e', color:'white',
          padding:'11px 20px', borderRadius:10, fontSize:13, fontWeight:700, zIndex:400,
          boxShadow:'0 8px 28px rgba(0,0,0,.25)', animation:'fadeUp .2s ease' }}>
          {toast}
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ padding:'18px 24px 14px', borderBottom:'1px solid var(--border)', background:'white', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:900, color:'var(--navy)', lineHeight:1 }}>
              Children
            </div>
            <div style={{ fontSize:12, color:'var(--mist)', marginTop:3 }}>
              {(children||[]).length} profiles · {filtered.length !== (children||[]).length ? `${filtered.length} shown` : 'all shown'}
            </div>
          </div>
          <div style={{ position:'relative' }}>
            <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:13, pointerEvents:'none' }}>🔍</span>
            <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name…"
              style={{ paddingLeft:32, paddingRight:10, height:36, border:'1.5px solid var(--border)', borderRadius:8, fontSize:12, outline:'none', width:180, background:'var(--fog)' }} />
            {search && (
              <span onClick={() => setSearch('')}
                style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', cursor:'pointer', color:'var(--mist)', fontSize:14 }}>×</span>
            )}
          </div>
          <select value={filterDis} onChange={e => setFilterDis(e.target.value)}
            style={{ height:36, border:'1.5px solid var(--border)', borderRadius:8, fontSize:12, padding:'0 10px', background:'var(--fog)', color: filterDis ? 'var(--navy)' : 'var(--mist)', outline:'none' }}>
            <option value="">All disabilities</option>
            {allDisabilities.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <div style={{ display:'flex', border:'1.5px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
            {[['grid','⊞'],['list','≡']].map(([m,icon]) => (
              <button key={m} onClick={() => setViewMode(m)}
                style={{ padding:'7px 11px', border:'none', background: viewMode===m ? 'var(--navy)' : 'var(--fog)', color: viewMode===m ? 'white' : 'var(--mist)', cursor:'pointer', fontSize:14, lineHeight:1 }}>
                {icon}
              </button>
            ))}
          </div>
          <button onClick={() => setShowAdd(true)}
            style={{ background:'var(--navy)', color:'white', border:'none', borderRadius:8,
              padding:'8px 16px', fontWeight:700, fontSize:12, cursor:'pointer', whiteSpace:'nowrap', height:36 }}>
            + Add Child
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

        {/* ── Left panel: card list ── */}
        <div style={{
          width: detailOpen ? 300 : '100%',
          flexShrink:0, overflowY:'auto',
          borderRight: detailOpen ? '1px solid var(--border)' : 'none',
          background:'var(--fog)', padding:12, transition:'width .2s'
        }}>
          {filtered.length === 0 && (
            <div style={{ textAlign:'center', padding:'40px 16px', color:'var(--mist)' }}>
              <div style={{ fontSize:28, marginBottom:8 }}>🔍</div>
              <div style={{ fontSize:13, fontWeight:600 }}>No results</div>
            </div>
          )}
          {viewMode === 'grid' ? (
            <div style={{
              display:'grid',
              gridTemplateColumns: detailOpen ? '1fr' : 'repeat(auto-fill,minmax(220px,1fr))',
              gap:8
            }}>
              {filtered.map(c => (
                <ChildCard
                  key={c._id} c={c} sel={sel} setSel={setSel}
                  record={records[c._id]}
                  narrow={detailOpen}
                />
              ))}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {filtered.map(c => (
                <ChildRow key={c._id} c={c} sel={sel} setSel={setSel} record={records[c._id]} />
              ))}
            </div>
          )}
        </div>

        {/* ── Right panel: detail ── */}
        {detailOpen && (
          <div style={{ flex:1, overflowY:'auto', padding:24 }} key={child._id}>
            <ChildDetail
              child={child}
              record={records[child._id]}
              alg={algData[child._id]}
              analytics={analytics[child._id]}
              onEdit={() => setEditing({ ...child })}
              onDelete={() => handleDelete(child)}
              onNav={onNav}
            />
          </div>
        )}

        {(children||[]).length === 0 && !showAdd && (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:14, color:'var(--mist)' }}>
            <div style={{ fontSize:52 }}>👶</div>
            <div style={{ fontSize:18, fontWeight:800, color:'var(--navy)' }}>No children yet</div>
            <div style={{ fontSize:13 }}>Add your first child profile to get started</div>
            <button onClick={() => setShowAdd(true)}
              style={{ background:'var(--navy)', color:'white', border:'none', borderRadius:9, padding:'10px 22px', fontWeight:700, fontSize:13, cursor:'pointer', marginTop:6 }}>
              + Add First Child
            </button>
          </div>
        )}
      </div>

      {showAdd && <ChildModal child={{ name:'', dob:'', gender:'prefer_not', disabilities:[], color:PALETTE[0] }} isNew saving={saving} onSave={handleAdd} onClose={() => setShowAdd(false)} currentUser={user} />}
      {editing  && <ChildModal child={editing} isNew={false} saving={saving} onSave={handleUpdate} onClose={() => setEditing(null)} currentUser={user} />}

      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
        @keyframes spin   { to { transform:rotate(360deg) } }
      `}</style>
    </div>
  );
}

// ── Child Card ─────────────────────────────────────────────────────────────────
function ChildCard({ c, sel, setSel, record, narrow = false }) {
  const age      = calcAge(c.dob);
  const isActive = sel === c._id;
  const scores   = domainScores(record?.indicators);
  const overall  = overallScore(scores);
  const maxTags  = narrow ? 1 : 2;
  const extraTags = (c.disabilities||[]).length - maxTags;

  return (
    <div
      onClick={() => setSel(c._id)}
      style={{
        background:'white', borderRadius:11,
        padding: narrow ? '10px 11px' : '12px 14px',
        cursor:'pointer', transition:'all .15s',
        border: isActive ? `2px solid ${c.color || '#1A4A6E'}` : '1.5px solid var(--border)',
        boxShadow: isActive ? `0 4px 18px ${c.color || '#1A4A6E'}28` : '0 1px 4px rgba(0,0,0,.05)',
      }}
    >
      <div style={{ display:'flex', alignItems:'center', gap: narrow ? 8 : 10, marginBottom: narrow ? 5 : 8 }}>
        <Avatar name={c.name} color={c.color} size={narrow ? 32 : 38} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{
            fontWeight:800, fontSize: narrow ? 12 : 13,
            color:'var(--navy)', overflow:'hidden',
            textOverflow:'ellipsis', whiteSpace:'nowrap'
          }}>
            {c.name}
          </div>
          <div style={{ fontSize:10, color:'var(--mist)', marginTop:1, display:'flex', alignItems:'center', gap:4, flexWrap:'nowrap' }}>
            {age ? `Age ${age}` : '—'}
            {c.gender && c.gender !== 'prefer_not' ? ` · ${c.gender.charAt(0).toUpperCase()}` : ''}
            {narrow && c.classroom && (
              <span style={{
                fontSize:9, background:'var(--fog)', color:'var(--navy)',
                fontWeight:700, padding:'1px 5px', borderRadius:5,
                border:'1px solid var(--border)', whiteSpace:'nowrap',
                overflow:'hidden', textOverflow:'ellipsis', maxWidth:80,
              }}>
                🏛️ {c.classroom?.name || 'Assigned'}
              </span>
            )}
          </div>
        </div>
        {overall !== null && (
          <div style={{ fontSize: narrow ? 12 : 13, fontWeight:900, color: scoreColor(overall), flexShrink:0 }}>
            {overall}%
          </div>
        )}
      </div>

      {overall !== null && (
        <div style={{ display:'flex', flexDirection:'column', gap: narrow ? 2 : 3, marginBottom: narrow ? 5 : 8 }}>
          {DOMAIN_IDS.map(d => (
            <div key={d} style={{ display:'flex', alignItems:'center', gap:5 }}>
              <div style={{
                width: narrow ? 48 : 60, fontSize:9, color:'var(--mist)',
                flexShrink:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'
              }}>
                {DOMAIN_LABELS[d].split(' ')[0]}
              </div>
              <div style={{ flex:1, height: narrow ? 3 : 4, background:'var(--fog)', borderRadius:3 }}>
                <div style={{
                  width: scores[d] != null ? `${scores[d]}%` : '0%',
                  height:'100%', background: DOMAIN_COLORS[d],
                  borderRadius:3, transition:'width .4s'
                }} />
              </div>
              <div style={{ fontSize:9, color:'var(--mist)', width:24, textAlign:'right', flexShrink:0 }}>
                {scores[d] != null ? `${scores[d]}%` : '—'}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
        {(c.disabilities||[]).slice(0, maxTags).map(d => (
          <span key={d} style={{
            fontSize: narrow ? 9 : 10,
            background:`${c.color||'#1A4A6E'}15`,
            color: c.color||'#1A4A6E',
            fontWeight:700,
            padding: narrow ? '1px 6px' : '2px 7px',
            borderRadius:8,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
            maxWidth: narrow ? 120 : 'none',
          }}>
            {d}
          </span>
        ))}
        {extraTags > 0 && (
          <span style={{ fontSize:10, color:'var(--mist)', padding:'2px 4px' }}>+{extraTags}</span>
        )}
      </div>
    </div>
  );
}

// ── Child Row (list view) ─────────────────────────────────────────────────────
function ChildRow({ c, sel, setSel, record }) {
  const age = calcAge(c.dob);
  const isActive = sel === c._id;
  const scores = domainScores(record?.indicators);
  const overall = overallScore(scores);
  return (
    <div onClick={() => setSel(c._id)}
      style={{ display:'flex', alignItems:'center', gap:11, padding:'9px 12px', borderRadius:9, cursor:'pointer',
        background: isActive ? 'white' : 'transparent',
        border: isActive ? `1.5px solid ${c.color||'var(--border)'}` : '1.5px solid transparent',
        transition:'all .12s',
      }}>
      <Avatar name={c.name} color={c.color} size={32} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:700, fontSize:12, color:'var(--navy)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.name}</div>
        <div style={{ fontSize:10, color:'var(--mist)' }}>
          {age ? `Age ${age}` : '—'}
          {c.classroom?.name ? ` · 🏛️ ${c.classroom.name}` : ''}
        </div>
      </div>
      {overall !== null && (
        <div style={{ fontSize:12, fontWeight:800, color: scoreColor(overall), flexShrink:0 }}>{overall}%</div>
      )}
      <div style={{ display:'flex', gap:3 }}>
        {(c.disabilities||[]).slice(0,1).map(d => (
          <span key={d} style={{ fontSize:9, background:`${c.color||'#1A4A6E'}15`, color: c.color||'#1A4A6E', fontWeight:700, padding:'2px 6px', borderRadius:6 }}>{d}</span>
        ))}
      </div>
    </div>
  );
}

// ── Algorithm Panel ───────────────────────────────────────────────────────────
function AlgorithmPanel({ alg, childName }) {
  if (!alg) {
    return (
      <div style={{ background:'white', borderRadius:13, padding:'14px 20px', border:'1.5px solid var(--border)', marginBottom:14, display:'flex', alignItems:'center', gap:10, color:'var(--mist)', fontSize:12 }}>
        <div style={{ width:14, height:14, border:'2px solid #dce3f5', borderTop:'2px solid #003366', borderRadius:'50%', animation:'spin .7s linear infinite', flexShrink:0 }} />
        Loading algorithm data…
      </div>
    );
  }

  const queue    = alg.queue;
  const sessions = alg.recentSessions || [];
  const totalDue = queue?.totalDue || 0;
  const groups   = queue?.queue    || [];

  const allMastery = groups.map(g => g.masteryAvg || 0);
  const avgMastery = allMastery.length
    ? Math.round(allMastery.reduce((a, b) => a + b, 0) / allMastery.length * 100)
    : null;

  const masteryColor = avgMastery === null ? '#ccc'
    : avgMastery >= 70 ? '#1B6B45'
    : avgMastery >= 40 ? '#D4AC0D'
    : '#C0392B';

  const lastSession  = sessions[0];
  const lastAccuracy = sessionAccuracy(lastSession);
  const lastRT       = sessions.map(s => s.avgResponseMs).find(rt => rt && rt > 0);
  const lastRTStr    = fmtResponseTime(lastRT);

  return (
    <div style={{ background:'white', borderRadius:13, padding:'16px 20px', border:'1.5px solid var(--border)', marginBottom:14 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div style={{ fontWeight:800, fontSize:11, color:'var(--mist)', letterSpacing:.7, textTransform:'uppercase' }}>
          🧠 Algorithm Data (SM-2 · BKT · Elo)
        </div>
        {avgMastery !== null && (
          <div style={{ fontSize:12, fontWeight:900, color:masteryColor, background:`${masteryColor}15`, padding:'2px 10px', borderRadius:8 }}>
            Avg Mastery {avgMastery}%
          </div>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: lastRTStr ? 'repeat(4,1fr)' : 'repeat(3,1fr)', gap:10, marginBottom: totalDue > 0 || sessions.length > 0 ? 14 : 0 }}>
        <div style={{ borderRadius:10, padding:'10px 12px', background: totalDue > 0 ? '#FFF3E0' : '#F0FFF4', border:`1.5px solid ${totalDue > 0 ? '#FFB74D' : '#A8E6C0'}`, textAlign:'center' }}>
          <div style={{ fontSize:20, fontWeight:900, color: totalDue > 0 ? '#E65100' : '#1B6B45', lineHeight:1 }}>{totalDue}</div>
          <div style={{ fontSize:9, fontWeight:700, color: totalDue > 0 ? '#E65100' : '#1B6B45', marginTop:3, textTransform:'uppercase', letterSpacing:.5 }}>
            {totalDue === 0 ? '✓ All caught up' : 'Cards Due'}
          </div>
        </div>
        <div style={{ borderRadius:10, padding:'10px 12px', background:'#EBF4FB', border:'1.5px solid #BDD7EE', textAlign:'center' }}>
          <div style={{ fontSize:20, fontWeight:900, color:'#003366', lineHeight:1 }}>{sessions.length}</div>
          <div style={{ fontSize:9, fontWeight:700, color:'#1A4A6E', marginTop:3, textTransform:'uppercase', letterSpacing:.5 }}>Recent Sessions</div>
        </div>
        <div style={{ borderRadius:10, padding:'10px 12px', background:'#F5EEF8', border:'1.5px solid #D7BDE2', textAlign:'center' }}>
          <div style={{ fontSize:20, fontWeight:900, color:'#7B2D8B', lineHeight:1 }}>
            {lastAccuracy !== null ? `${lastAccuracy}%` : '—'}
          </div>
          <div style={{ fontSize:9, fontWeight:700, color:'#7B2D8B', marginTop:3, textTransform:'uppercase', letterSpacing:.5 }}>Last Accuracy</div>
        </div>
        {lastRTStr && (
          <div style={{ borderRadius:10, padding:'10px 12px', background:'#E0F2F1', border:'1.5px solid #80CBC4', textAlign:'center' }}>
            <div style={{ fontSize:20, fontWeight:900, color:'#0D5C63', lineHeight:1 }}>{lastRTStr}</div>
            <div style={{ fontSize:9, fontWeight:700, color:'#0D5C63', marginTop:3, textTransform:'uppercase', letterSpacing:.5 }}>Avg Response</div>
          </div>
        )}
      </div>

      {totalDue > 0 && groups.length > 0 && (
        <div style={{ marginBottom: sessions.length > 0 ? 12 : 0 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--mist)', textTransform:'uppercase', letterSpacing:.6, marginBottom:6 }}>Due by Set</div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {groups.slice(0, 4).map((g, i) => {
              const mPct = Math.round((g.masteryAvg || 0) * 100);
              const mCol = mPct >= 70 ? '#1B6B45' : mPct >= 40 ? '#D4AC0D' : '#C0392B';
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', background:'#fafbff', borderRadius:8, border:'1px solid #e8edf8' }}>
                  <div style={{ flex:1, fontSize:11, fontWeight:600, color:'var(--navy)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {g.setTitle || 'Untitled set'}
                  </div>
                  <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                    <span style={{ fontSize:10, fontWeight:700, color:'#E65100', background:'#FFF3E0', padding:'1px 7px', borderRadius:10 }}>⏰ {g.dueCount}</span>
                    <span style={{ fontSize:10, fontWeight:700, color:mCol, background:`${mCol}15`, padding:'1px 7px', borderRadius:10 }}>{mPct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sessions.length > 0 && (
        <div>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--mist)', textTransform:'uppercase', letterSpacing:.6, marginBottom:6 }}>Recent Sessions</div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {sessions.slice(0, 3).map((s, i) => {
              const acc    = sessionAccuracy(s);
              const accCol = acc === null ? '#aaa' : acc >= 70 ? '#1B6B45' : acc >= 40 ? '#D4AC0D' : '#C0392B';
              const rtStr  = fmtResponseTime(s.avgResponseMs);
              const modeIcon = s.moduleType === 'quiz'        ? '🎯'
  : s.moduleType === 'match'         ? '🧩'
  : s.moduleType === 'worksheet'     ? '📝'
  : s.moduleType === 'activitycard'  ? '🖼️'
  : s.moduleType === 'storybook'     ? '📖'
  : '🃏';
              const dateStr  = s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '—';
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', background:'#fafbff', borderRadius:8, border:'1px solid #e8edf8' }}>
                  <span style={{ fontSize:14 }}>{modeIcon}</span>
                  <div style={{ flex:1, fontSize:11, color:'var(--navy)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    <span style={{ fontWeight:700 }}>{s.setTitle || 'Session'}</span>
                    <span style={{ color:'var(--mist)', marginLeft:5, fontWeight:400 }}>{dateStr}</span>
                  </div>
                  <div style={{ display:'flex', gap:5, flexShrink:0, alignItems:'center' }}>
                    {acc !== null && (
                      <span style={{ fontSize:10, fontWeight:700, color:accCol, background:`${accCol}15`, padding:'1px 7px', borderRadius:10 }}>{acc}%</span>
                    )}
                    {rtStr && (
                      <span style={{ fontSize:9, fontWeight:700, color:'#0D5C63', background:'#E0F2F1', padding:'1px 6px', borderRadius:8 }}>⚡{rtStr}</span>
                    )}
                    <span style={{ fontSize:9, color:'var(--mist)', fontWeight:600 }}>{s.ratedCount || s.totalCards || 0} cards</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {totalDue === 0 && sessions.length === 0 && (
        <div style={{ textAlign:'center', padding:'8px 0 4px', color:'var(--mist)', fontSize:12 }}>
          No sessions yet — start a Learning Activity to begin tracking
        </div>
      )}
    </div>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────────
function AnalyticsTab({ child, analytics, alg, onNav }) {
  const data     = analytics;
  const sessions = data?.sessions || [];
  const summary  = data?.summary;
  const queue    = alg?.queue;
  const firstName = child.name.split(' ')[0];

  if (data === undefined || data === null) {
    return (
      <div style={{ marginTop:14, background:'white', borderRadius:13, padding:32, border:'1.5px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', gap:10, color:'var(--mist)' }}>
        <div style={{ width:16, height:16, border:'2px solid #dce3f5', borderTop:'2px solid #003366', borderRadius:'50%', animation:'spin .7s linear infinite' }} />
        Loading analytics…
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div style={{ marginTop:14, background:'white', borderRadius:13, padding:'40px 24px', border:'1.5px solid var(--border)', textAlign:'center' }}>
        <div style={{ fontSize:36, marginBottom:10 }}>📊</div>
        <div style={{ fontSize:15, fontWeight:800, color:'var(--navy)', marginBottom:6 }}>No sessions yet</div>
        <div style={{ fontSize:12, color:'var(--mist)', marginBottom:18 }}>Start a learning activity with {firstName} to begin tracking analytics.</div>
        <button onClick={() => onNav('learn')} style={{ padding:'10px 22px', background:'var(--navy)', color:'white', border:'none', borderRadius:9, fontWeight:700, fontSize:13, cursor:'pointer' }}>
          🎮 Start Learning Activity
        </button>
      </div>
    );
  }

  const totalSessions = sessions.length;
  const totalCards    = sessions.reduce((s, x) => s + (x.ratedCount ?? x.totalCards ?? 0), 0);
  const totalCorrect  = sessions.reduce((s, x) => s + (x.correctCount || 0), 0);
  const avgAccuracy   = totalCards > 0 ? Math.round(totalCorrect / totalCards * 100) : null;

  const summaryRT      = summary?.analytics?.overallAvgResponseMs ?? summary?.overallAvgResponseMs ?? null;
  const sessionsWithRT = sessions.filter(s => s.avgResponseMs > 0);
  const sessionAvgRT   = sessionsWithRT.length
    ? Math.round(sessionsWithRT.reduce((a, s) => a + s.avgResponseMs, 0) / sessionsWithRT.length) : null;
  const trendWithRT    = (summary?.analytics?.accuracyTrend || summary?.accuracyTrend || []).filter(t => t.avgResponseMs > 0);
  const trendAvgRT     = trendWithRT.length
    ? Math.round(trendWithRT.reduce((a, t) => a + t.avgResponseMs, 0) / trendWithRT.length) : null;
  const rawAvgRT       = summaryRT ?? sessionAvgRT ?? trendAvgRT;
  const avgRT          = rawAvgRT ? Math.round(rawAvgRT / 100) / 10 : null;
  const avgRTStr       = avgRT !== null ? `${avgRT}s` : '—';

  const trendSessions = [...sessions].reverse().slice(0, 10);
  const trendData     = trendSessions.map(s => {
    const rated    = s.ratedCount ?? s.totalCards ?? 0;
    const accuracy = rated > 0 ? Math.round((s.correctCount || 0) / rated * 100) : null;
    return {
      date:     s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '—',
      accuracy, cards: rated,
      title:    s.setTitle || 'Session',
      mode:     s.moduleType || 'flashcard',
      rt:       fmtResponseTime(s.avgResponseMs),
    };
  });

  const masteryBySets = summary?.analytics?.masteryBySet || summary?.masteryBySet || [];
  const totalDue      = queue?.totalDue || 0;
  const dueGroups     = queue?.queue    || [];

  if (sessions.length >= 2) {
    const oldest = new Date(sessions[sessions.length - 1].createdAt);
    const newest = new Date(sessions[0].createdAt);
    const weeks  = Math.max(1, (newest - oldest) / (7 * 24 * 3600000));
    var sessPerWeek = Math.round(sessions.length / weeks * 10) / 10;
  } else { var sessPerWeek = sessions.length; }

  const level = avgAccuracy === null ? null
    : avgAccuracy >= 80 ? { label:'Advanced',   color:'#1B6B45', bg:'#EDF7F2', desc:'Consistently correct, ready for harder sets' }
    : avgAccuracy >= 60 ? { label:'Developing', color:'#D4AC0D', bg:'#FEF9E7', desc:'Making progress, occasional prompts needed' }
    : avgAccuracy >= 40 ? { label:'Emerging',   color:'#E65100', bg:'#FFF3E0', desc:'Early stage, frequent support required' }
    :                     { label:'Foundation', color:'#C0392B', bg:'#FDEDEC', desc:'Needs intensive support and scaffolding' };

  const barColor = (pct) => pct >= 70 ? '#1B6B45' : pct >= 40 ? '#D4AC0D' : '#C0392B';

  const SectionHead = ({ label }) => (
    <div style={{ fontWeight:800, fontSize:11, color:'var(--mist)', letterSpacing:.7, textTransform:'uppercase', marginBottom:12 }}>{label}</div>
  );

  const StatCard = ({ icon, val, label, color='var(--navy)', bg='#f0f4ff', sub, highlight }) => (
    <div style={{ borderRadius:12, padding:'12px 14px', background:bg, border:`1px solid ${color}20`, textAlign:'center',
                  outline: highlight ? `2px solid ${color}60` : 'none' }}>
      <div style={{ fontSize:18, marginBottom:2 }}>{icon}</div>
      <div style={{ fontSize:22, fontWeight:900, color, lineHeight:1 }}>{val}</div>
      <div style={{ fontSize:9, fontWeight:700, color, marginTop:3, textTransform:'uppercase', letterSpacing:.5 }}>{label}</div>
      {sub && <div style={{ fontSize:10, color:'var(--mist)', marginTop:2 }}>{sub}</div>}
    </div>
  );

  const hasRT = avgRT !== null;

  return (
    <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ background:'white', borderRadius:13, padding:'16px 20px', border:'1.5px solid var(--border)' }}>
        <SectionHead label={`${firstName}'s Learning Overview`} />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom: level ? 14 : 0 }}>
          <StatCard icon="🗓️" val={totalSessions}  label="Sessions"    color="#1A4A6E" bg="#EBF4FB" />
          <StatCard icon="🃏" val={totalCards}      label="Cards Done"  color="#7B2D8B" bg="#F5EEF8" />
          <StatCard icon="🎯"
            val={avgAccuracy !== null ? `${avgAccuracy}%` : '—'}
            label="Avg Accuracy"
            color={avgAccuracy !== null ? barColor(avgAccuracy) : '#aaa'}
            bg={avgAccuracy !== null ? `${barColor(avgAccuracy)}12` : '#f5f5f5'} />
          <StatCard icon="⚡" val={avgRTStr} label="Avg Response"
            color={hasRT ? '#0D5C63' : '#aaa'} bg={hasRT ? '#E0F2F1' : '#f5f5f5'}
            sub="per card" highlight={hasRT} />
        </div>
        {level && (
          <div style={{ display:'flex', gap:10 }}>
            <div style={{ flex:1, borderRadius:10, padding:'10px 14px', background:level.bg, border:`1.5px solid ${level.color}30`, display:'flex', alignItems:'center', gap:10 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:800, color:level.color }}>{level.label} Level</div>
                <div style={{ fontSize:10, color:'var(--mist)', marginTop:2 }}>{level.desc}</div>
              </div>
            </div>
            <div style={{ borderRadius:10, padding:'10px 14px', background:'#f8f9ff', border:'1.5px solid #dce3f5', textAlign:'center', minWidth:90 }}>
              <div style={{ fontSize:18, fontWeight:900, color:'var(--navy)' }}>{sessPerWeek}</div>
              <div style={{ fontSize:9, fontWeight:700, color:'var(--mist)', textTransform:'uppercase', letterSpacing:.5 }}>Sessions/week</div>
            </div>
          </div>
        )}
        {hasRT && (
          <div style={{ marginTop:10, padding:'8px 12px', background:'#E0F2F1', borderRadius:8, fontSize:11, color:'#0D5C63', fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
            <span>⚡</span>
            <span>Average response time of <strong>{avgRTStr}/card</strong> across {sessionsWithRT.length || trendWithRT.length} session{(sessionsWithRT.length || trendWithRT.length) !== 1 ? 's' : ''}.
              {avgRT < 3 ? ' Quick responses — great recall!' : avgRT < 6 ? ' Moderate pace — good focus.' : ' Slower pace — may need more practice.'}
            </span>
          </div>
        )}
      </div>

      {trendData.length > 0 && (
        <div style={{ background:'white', borderRadius:13, padding:'20px 20px', border:'1.5px solid var(--border)' }}>
          <SectionHead label="Accuracy Trend (Last 10 Sessions)" />
          <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:100 }}>
            {trendData.map((d, i) => (
              <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                <div style={{ fontSize:9, fontWeight:700, color: d.accuracy !== null ? barColor(d.accuracy) : '#ccc' }}>
                  {d.accuracy !== null ? `${d.accuracy}%` : '—'}
                </div>
                <div style={{ width:'100%', borderRadius:'4px 4px 0 0',
                  height: d.accuracy !== null ? `${Math.max(6, d.accuracy * 0.6)}px` : '4px',
                  background: d.accuracy !== null ? barColor(d.accuracy) : '#eee', transition:'height .3s', position:'relative' }}
                  title={`${d.title} · ${d.date} · ${d.cards} cards${d.rt ? ` · ⚡${d.rt}/card` : ''}`} />
                <div style={{ fontSize:8, color:'var(--mist)', textAlign:'center', whiteSpace:'nowrap', overflow:'hidden', maxWidth:'100%', textOverflow:'ellipsis' }}>{d.date}</div>
                {d.rt && <div style={{ fontSize:7, color:'#0D5C63', fontWeight:700, marginTop:-2 }}>⚡{d.rt}</div>}
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:12, marginTop:10, fontSize:10, color:'var(--mist)', flexWrap:'wrap' }}>
            {[['#1B6B45','≥70% Good'],['#D4AC0D','40–69% Fair'],['#C0392B','<40% Needs support']].map(([c,l]) => (
              <span key={l} style={{ display:'flex', alignItems:'center', gap:4 }}>
                <span style={{ width:8, height:8, borderRadius:2, background:c, display:'inline-block' }} />{l}
              </span>
            ))}
            {trendData.some(d => d.rt) && (
              <span style={{ display:'flex', alignItems:'center', gap:4, color:'#0D5C63' }}>
                <span>⚡</span> Response time per session
              </span>
            )}
          </div>
        </div>
      )}

      {masteryBySets.length > 0 && (
        <div style={{ background:'white', borderRadius:13, padding:'16px 20px', border:'1.5px solid var(--border)' }}>
          <SectionHead label="BKT Mastery by Flashcard Set" />
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {masteryBySets.map((s, i) => {
              const pct = Math.round((s.masteryAvg || 0) * 100);
              const col = barColor(pct);
              return (
                <div key={i}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--navy)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{s.setTitle || 'Untitled'}</span>
                    <span style={{ fontSize:12, fontWeight:900, color:col, marginLeft:8 }}>{pct}%</span>
                  </div>
                  <div style={{ height:7, background:'var(--fog)', borderRadius:4 }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:col, borderRadius:4, transition:'width .4s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ background:'white', borderRadius:13, padding:'16px 20px', border:'1.5px solid var(--border)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <SectionHead label="Spaced Repetition Schedule (SM-2)" />
          {totalDue > 0 && (
            <span style={{ fontSize:10, fontWeight:700, color:'#E65100', background:'#FFF3E0', padding:'2px 9px', borderRadius:10 }}>
              ⏰ {totalDue} due today
            </span>
          )}
        </div>
        {totalDue === 0 ? (
          <div style={{ display:'flex', alignItems:'center', gap:8, color:'#1B6B45', fontSize:12, fontWeight:600 }}>
            <span style={{ fontSize:20 }}>✅</span> All caught up — no cards due today. Great retention!
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {dueGroups.map((g, i) => {
              const mp = Math.round((g.masteryAvg || 0) * 100);
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'#fafbff', borderRadius:9, border:'1px solid #e8edf8' }}>
                  <span style={{ fontSize:14 }}>🃏</span>
                  <div style={{ flex:1, fontSize:12, fontWeight:600, color:'var(--navy)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.setTitle || 'Untitled'}</div>
                  <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                    <span style={{ fontSize:10, fontWeight:700, color:'#E65100', background:'#FFF3E0', padding:'2px 7px', borderRadius:8 }}>⏰ {g.dueCount} cards</span>
                    <span style={{ fontSize:10, fontWeight:700, color: barColor(mp), background:`${barColor(mp)}15`, padding:'2px 7px', borderRadius:8 }}>M: {mp}%</span>
                  </div>
                </div>
              );
            })}
            <button onClick={() => onNav('learn')} style={{ marginTop:4, padding:'9px', background:'var(--navy)', color:'white', border:'none', borderRadius:9, fontWeight:700, fontSize:12, cursor:'pointer' }}>
              🎮 Start Review Session Now
            </button>
          </div>
        )}
      </div>

      <div style={{ background:'white', borderRadius:13, padding:'16px 20px', border:'1.5px solid var(--border)' }}>
        <SectionHead label="Session History" />
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {sessions.map((s, i) => {
            const rated   = s.ratedCount ?? s.totalCards ?? 0;
            const acc     = rated > 0 ? Math.round((s.correctCount || 0) / rated * 100) : null;
            const col     = acc !== null ? barColor(acc) : '#aaa';
            const rtStr   = fmtResponseTime(s.avgResponseMs);
            const icon    = s.moduleType === 'quiz'        ? '🎯'
  : s.moduleType === 'match'         ? '🧩'
  : s.moduleType === 'flip'          ? '🃏'
  : s.moduleType === 'worksheet'     ? '📝'
  : s.moduleType === 'activitycard'  ? '🖼️'
  : s.moduleType === 'storybook'     ? '📖'
  : '🃏';
const mLabel  = s.moduleType === 'quiz'        ? 'Quiz'
  : s.moduleType === 'match'         ? 'Match'
  : s.moduleType === 'flip'          ? 'Flip'
  : s.moduleType === 'worksheet'     ? 'Worksheet'
  : s.moduleType === 'activitycard'  ? 'Activity Card'
  : s.moduleType === 'storybook'     ? 'Story Book'
  : 'Flashcard';
            const dateStr = s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—';
            const durStr  = s.durationMin ? `${s.durationMin}m` : null;
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background: i%2===0 ? '#fafbff' : 'white', borderRadius:8, border:'1px solid #f0f4f8' }}>
                <span style={{ fontSize:16 }}>{icon}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--navy)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {s.setTitle || 'Session'}
                    <span style={{ fontSize:10, fontWeight:400, color:'var(--mist)', marginLeft:6 }}>{mLabel}</span>
                  </div>
                  <div style={{ fontSize:10, color:'var(--mist)', marginTop:1 }}>
                    {dateStr}
                    {durStr ? ` · ${durStr}` : ''}
                    {rtStr  ? ` · ⚡${rtStr}/card` : ''}
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                  <span style={{ fontSize:10, color:'var(--mist)' }}>{rated} cards</span>
                  {acc !== null && (
                    <span style={{ fontSize:11, fontWeight:800, color:col, background:`${col}15`, padding:'2px 8px', borderRadius:8 }}>{acc}%</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Algorithm Tab ─────────────────────────────────────────────────────────────
function AlgorithmTab({ analytics, onNav }) {
  const summary         = analytics?.summary;
  const moduleAnalytics = summary?.analytics?.moduleAnalytics;

  if (analytics === undefined || analytics === null) {
    return (
      <div style={{ marginTop:14, background:'white', borderRadius:13, padding:32, border:'1.5px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', gap:10, color:'var(--mist)' }}>
        <div style={{ width:16, height:16, border:'2px solid #dce3f5', borderTop:'2px solid #003366', borderRadius:'50%', animation:'spin .7s linear infinite' }} />
        Loading algorithm data…
      </div>
    );
  }

  return (
    <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:14 }}>
      <LearningModulesSection moduleAnalytics={moduleAnalytics} onNav={onNav} />
      <AlgorithmAnalysisSection summary={summary} />
    </div>
  );
}

// ── Child Detail ──────────────────────────────────────────────────────────────
function ChildDetail({ child, record, alg, analytics, onEdit, onDelete, onNav }) {
  const age     = calcAge(child.dob);
  const scores  = domainScores(record?.indicators);
  const overall = overallScore(scores);
  const hasData = overall !== null;
  const [tab, setTab] = useState('profile');

  const TAB_BTN = (id, label) => (
    <button onClick={() => setTab(id)} style={{
      padding:'8px 16px', background:'none', border:'none',
      borderBottom: tab === id ? '2.5px solid var(--navy)' : '2.5px solid transparent',
      color: tab === id ? 'var(--navy)' : 'var(--mist)',
      fontWeight: tab === id ? 800 : 600, fontSize:12, cursor:'pointer',
      display:'flex', alignItems:'center', gap:5,
    }}>
      {label}
    </button>
  );

  return (
    <div style={{ maxWidth:760 }}>
      <div style={{ background:'white', borderRadius:14, padding:'20px 22px', border:'1.5px solid var(--border)', borderTop:`4px solid ${child.color||'var(--navy)'}`, marginBottom:0 }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:16 }}>
          <Avatar name={child.name} color={child.color} size={56} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:900, color:'var(--navy)' }}>{child.name}</div>
              {child.gender && child.gender !== 'prefer_not' && (
                <span style={{ fontSize:11, background:'var(--fog)', color:'var(--mist)', padding:'2px 9px', borderRadius:6, fontWeight:700, textTransform:'capitalize' }}>{child.gender}</span>
              )}
              {hasData && (
                <span style={{ fontSize:12, fontWeight:900, color: scoreColor(overall), background: `${scoreColor(overall)}15`, padding:'2px 10px', borderRadius:8 }}>
                  {overall}% Overall
                </span>
              )}
            </div>
            <div style={{ fontSize:12, color:'var(--mist)', marginTop:4 }}>
              {child.dob ? `Born ${new Date(child.dob).toLocaleDateString('en-IN')}` : 'No DOB'}
              {age ? ` · Age ${age}` : ''}
              {child.classroom?.name && (
                <span style={{ marginLeft:6, fontSize:11, fontWeight:700, color:'var(--navy)', background:'var(--fog)', padding:'1px 8px', borderRadius:6 }}>
                  🏛️ {child.classroom.name}
                </span>
              )}
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:8 }}>
              {(child.disabilities||[]).map(d => (
                <span key={d} style={{ fontSize:11, background:`${child.color||'#1A4A6E'}18`, color:child.color||'#1A4A6E', fontWeight:700, padding:'3px 10px', borderRadius:10 }}>{d}</span>
              ))}
            </div>
          </div>
          <div style={{ display:'flex', gap:7, flexShrink:0 }}>
            <button onClick={onEdit} style={{ padding:'7px 14px', background:'var(--fog)', color:'var(--navy)', border:'1.5px solid var(--border)', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>✎ Edit</button>
            <button onClick={onDelete} style={{ padding:'7px 10px', background:'#fff0f0', color:'#C0392B', border:'1.5px solid #f5c6c6', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>🗑</button>
          </div>
        </div>
        {child.clinicalNotes && (
          <div style={{ marginTop:14, padding:'10px 14px', background:'var(--fog)', borderRadius:9, fontSize:12, color:'var(--ink)', lineHeight:1.6, borderLeft:`3px solid ${child.color||'var(--navy)'}` }}>
            {child.clinicalNotes}
          </div>
        )}
        <div style={{ display:'flex', gap:0, marginTop:16, borderBottom:'1px solid var(--border)', marginLeft:-4 }}>
          {TAB_BTN('profile',    '📋 Profile & Progress')}
          {TAB_BTN('analytics',  '📈 Activity Analytics')}
          {TAB_BTN('algorithms', '🧠 Algorithms')}
        </div>
      </div>

      {tab === 'profile' && (
        <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{ background:'white', borderRadius:13, padding:'16px 20px', border:'1.5px solid var(--border)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
              <div style={{ fontWeight:800, fontSize:11, color:'var(--mist)', letterSpacing:.7, textTransform:'uppercase' }}>School Readiness Test - NCF Domain Overview</div>
              {hasData ? (
                <div style={{ display:'flex', gap:6 }}>
                  {DOMAIN_IDS.map(d => scores[d] != null && (
                    <div key={d} title={DOMAIN_LABELS[d]} style={{ fontSize:10, fontWeight:800, color:DOMAIN_COLORS[d], background:`${DOMAIN_COLORS[d]}15`, padding:'2px 7px', borderRadius:6 }}>
                      {DOMAIN_ICONS[d]} {scores[d]}%
                    </div>
                  ))}
                </div>
              ) : <span style={{ fontSize:11, color:'var(--mist)', fontStyle:'italic' }}>No progress data yet</span>}
            </div>
            {hasData ? (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {DOMAIN_IDS.map(d => {
                  const pct = scores[d];
                  if (pct === null) return null;
                  return (
                    <div key={d}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                        <span style={{ fontSize:12, fontWeight:700, color:'var(--ink)', display:'flex', alignItems:'center', gap:5 }}>
                          <span>{DOMAIN_ICONS[d]}</span> {DOMAIN_LABELS[d]}
                        </span>
                        <span style={{ fontSize:12, fontWeight:900, color:DOMAIN_COLORS[d] }}>{pct}%</span>
                      </div>
                      <div style={{ height:8, background:'var(--fog)', borderRadius:6 }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:DOMAIN_COLORS[d], borderRadius:6, transition:'width .5s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 24px' }}>
                {DOMAIN_IDS.map(d => (
                  <div key={d}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                      <span style={{ fontSize:12, fontWeight:600, color:'#bbb' }}>{DOMAIN_LABELS[d]}</span>
                      <span style={{ fontSize:10, color:'#ddd' }}>—</span>
                    </div>
                    <div style={{ height:6, background:'var(--fog)', borderRadius:4 }} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <AlgorithmPanel alg={alg} childName={child.name.split(' ')[0]} />

          <div style={{ background:'white', borderRadius:12, padding:'14px 18px', border:'1.5px solid var(--border)' }}>
            <div style={{ fontWeight:800, fontSize:11, color:'var(--mist)', letterSpacing:.7, textTransform:'uppercase', marginBottom:12 }}>
              Open Module for {child.name.split(' ')[0]}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
              {[
                { id:'learn',     icon:'🎮', label:'Learning Activity',   bg:'#FEF9E7', color:'#7B5800' },
                { id:'flashcard', icon:'🃏', label:'Flashcard Generator', bg:'#EDF7F2', color:'#1B6B45' },
                { id:'progress',  icon:'📊', label:'Progress Tracker',    bg:'#EFEBE9', color:'#5D4037' },
                { id:'workplan',  icon:'📅', label:'Weekly Work Plan',    bg:'#E8F8F5', color:'#0D5C63' },
              ].map(m => (
                <button key={m.id} onClick={() => onNav(m.id)}
                  style={{ display:'flex', alignItems:'center', gap:9, padding:'10px 14px',
                    background:m.bg, color:m.color, border:`1.5px solid ${m.color}25`,
                    borderRadius:9, fontWeight:700, fontSize:12, cursor:'pointer', textAlign:'left' }}>
                  <span style={{ fontSize:18 }}>{m.icon}</span>
                  <span>{m.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'analytics' && (
        <AnalyticsTab child={child} analytics={analytics} alg={alg} onNav={onNav} />
      )}

      {tab === 'algorithms' && (
        <AlgorithmTab analytics={analytics} onNav={onNav} />
      )}
    </div>
  );
}

// ── Child Modal ───────────────────────────────────────────────────────────────
function ChildModal({ child, isNew, saving, onSave, onClose, currentUser }) {
  const { getAuthHeader } = useAuth();
  const [f,          setF]          = useState({ ...child, classroomId: child.classroom?._id || child.classroom || '' });
  const [disIn,      setDisIn]      = useState('');
  const [classrooms, setClassrooms] = useState([]);
  const [crLoading,  setCrLoading]  = useState(false);

  useEffect(() => {
    if (!isNew) return;
    setCrLoading(true);

    // ── SCHOOL-SCOPED classroom fetch ─────────────────────────────────────────
    // Always pass ?school= so only classrooms from the user's school are returned.
    const schoolQ = currentUser?.school ? `?school=${currentUser.school}` : ''
    fetch(`${API}/classrooms${schoolQ}`, { headers: getAuthHeader() })
      .then(r => r.json())
      .then(d => {
        const all = Array.isArray(d?.classrooms) ? d.classrooms
                  : Array.isArray(d?.data)       ? d.data
                  : Array.isArray(d)              ? d
                  : [];

        // For teachers: further filter to only their assigned classrooms
        let filtered = all;
        if (currentUser?.role === 'teacher') {
          const assignedIds = new Set(
            (currentUser.classrooms || []).map(id => String(id))
          );
          filtered = all.filter(cr => assignedIds.has(String(cr._id)));
        }

        setClassrooms(filtered);

        // Auto-select if only one option
        if (filtered.length === 1 && !f.classroomId) {
          setF(p => ({ ...p, classroomId: filtered[0]._id }));
        }
      })
      .catch(() => {})
      .finally(() => setCrLoading(false));
  }, [isNew]);

  const addDis = () => {
    const v = disIn.trim();
    if (!v) return;
    setF(p => ({ ...p, disabilities: [...(p.disabilities||[]), v] }));
    setDisIn('');
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.52)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:300, backdropFilter:'blur(3px)' }}
      onClick={onClose}>
      <div style={{ background:'white', borderRadius:16, width:460, maxWidth:'95vw', maxHeight:'92vh',
        display:'flex', flexDirection:'column', boxShadow:'0 24px 70px rgba(0,0,0,.28)',
        animation:'fadeUp .18s ease', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:'16px 22px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:800, color:'var(--navy)' }}>
            {isNew ? '+ Add Child Profile' : `Edit: ${f.name||'Profile'}`}
          </div>
          <button onClick={onClose} style={{ background:'var(--fog)', border:'none', borderRadius:'50%', width:28, height:28, cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
        </div>
        <div style={{ overflowY:'auto', padding:'18px 22px', flex:1 }}>
          <div style={{ marginBottom:13 }}>
            <label style={{ fontSize:10, fontWeight:700, color:'var(--mist)', letterSpacing:.6, textTransform:'uppercase', display:'block', marginBottom:5 }}>Full Name *</label>
            <input value={f.name||''} onChange={e => setF(p=>({...p,name:e.target.value}))}
              placeholder="Child's full name"
              style={{ width:'100%', border:'1.5px solid var(--border)', borderRadius:8, padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:13 }}>
            <div>
              <label style={{ fontSize:10, fontWeight:700, color:'var(--mist)', letterSpacing:.6, textTransform:'uppercase', display:'block', marginBottom:5 }}>Date of Birth</label>
              <input type="date" value={f.dob ? f.dob.split('T')[0] : ''} onChange={e => setF(p=>({...p,dob:e.target.value}))}
                style={{ width:'100%', border:'1.5px solid var(--border)', borderRadius:8, padding:'8px 11px', fontSize:13, outline:'none' }} />
            </div>
            <div>
              <label style={{ fontSize:10, fontWeight:700, color:'var(--mist)', letterSpacing:.6, textTransform:'uppercase', display:'block', marginBottom:5 }}>Gender</label>
              <select value={f.gender||'prefer_not'} onChange={e => setF(p=>({...p,gender:e.target.value}))}
                style={{ width:'100%', border:'1.5px solid var(--border)', borderRadius:8, padding:'8px 11px', fontSize:13, outline:'none', background:'white' }}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not">Prefer not to say</option>
              </select>
            </div>
          </div>

          {/* ── Classroom picker — only shown when adding a new child ── */}
          {isNew && (
            <div style={{ marginBottom:13 }}>
              <label style={{ fontSize:10, fontWeight:700, color:'var(--mist)', letterSpacing:.6, textTransform:'uppercase', display:'block', marginBottom:5 }}>
                Assign to Classroom
                {classrooms.length === 0 && !crLoading && (
                  <span style={{ marginLeft:6, fontSize:9, color:'#E65100', fontWeight:600, textTransform:'none' }}>
                    (no classrooms found — contact admin)
                  </span>
                )}
              </label>
              {crLoading ? (
                <div style={{ fontSize:12, color:'var(--mist)', padding:'8px 0' }}>Loading classrooms…</div>
              ) : classrooms.length === 0 ? (
                <div style={{ fontSize:12, color:'var(--mist)', background:'var(--fog)', borderRadius:8, padding:'8px 12px' }}>
                  No classrooms assigned to you yet.
                </div>
              ) : classrooms.length === 1 ? (
                <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
                              background:'#EBF4FB', border:'1.5px solid #4A90D9', borderRadius:8 }}>
                  <span style={{ fontSize:14 }}>🏛️</span>
                  <span style={{ fontSize:13, fontWeight:700, color:'var(--navy)' }}>{classrooms[0].name}</span>
                  {classrooms[0].grade && (
                    <span style={{ fontSize:11, color:'var(--mist)' }}>
                      {classrooms[0].grade}{classrooms[0].section ? ` · ${classrooms[0].section}` : ''}
                    </span>
                  )}
                  <span style={{ marginLeft:'auto', fontSize:10, color:'#4A90D9', fontWeight:700 }}>Auto-selected</span>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {classrooms.map(cr => {
                    const selected = f.classroomId === cr._id;
                    return (
                      <div key={cr._id} onClick={() => setF(p => ({ ...p, classroomId: cr._id }))}
                        style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px',
                                 borderRadius:9, cursor:'pointer', transition:'all .12s',
                                 border:`1.5px solid ${selected ? '#4A90D9' : 'var(--border)'}`,
                                 background: selected ? '#EBF4FB' : 'white' }}>
                        <div style={{ width:16, height:16, borderRadius:'50%', flexShrink:0,
                                      border:`2px solid ${selected ? '#4A90D9' : 'var(--border)'}`,
                                      background: selected ? '#4A90D9' : 'white',
                                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                          {selected && <div style={{ width:6, height:6, borderRadius:'50%', background:'white' }} />}
                        </div>
                        <span style={{ fontSize:13, fontWeight: selected ? 700 : 500, color: selected ? 'var(--navy)' : 'var(--ink)' }}>
                          🏛️ {cr.name}
                        </span>
                        {(cr.grade || cr.section) && (
                          <span style={{ fontSize:11, color:'var(--mist)' }}>
                            {[cr.grade, cr.section].filter(Boolean).join(' · ')}
                          </span>
                        )}
                        {cr.academicYear && (
                          <span style={{ marginLeft:'auto', fontSize:10, color:'var(--mist)' }}>{cr.academicYear}</span>
                        )}
                      </div>
                    );
                  })}
                  {!f.classroomId && (
                    <div style={{ fontSize:11, color:'#E65100', marginTop:2 }}>⚠️ Please select a classroom</div>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom:13 }}>
            <label style={{ fontSize:10, fontWeight:700, color:'var(--mist)', letterSpacing:.6, textTransform:'uppercase', display:'block', marginBottom:7 }}>Profile Colour</label>
            <div style={{ display:'flex', gap:7, flexWrap:'wrap', alignItems:'center' }}>
              {PALETTE.map(c => (
                <div key={c} onClick={() => setF(p=>({...p,color:c}))}
                  style={{ width:24, height:24, borderRadius:'50%', background:c, cursor:'pointer',
                    border: f.color===c ? '3px solid var(--ink)' : '2px solid transparent',
                    transform: f.color===c ? 'scale(1.2)' : 'scale(1)', transition:'all .12s' }} />
              ))}
              <input type="color" value={f.color||PALETTE[0]} onChange={e => setF(p=>({...p,color:e.target.value}))}
                title="Custom colour"
                style={{ width:24, height:24, borderRadius:'50%', border:'none', padding:1, cursor:'pointer', background:'none' }} />
            </div>
          </div>

          <div style={{ marginBottom:13 }}>
            <label style={{ fontSize:10, fontWeight:700, color:'var(--mist)', letterSpacing:.6, textTransform:'uppercase', display:'block', marginBottom:7 }}>Disabilities / Conditions</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:8 }}>
              {DISABILITIES.map(d => {
                const isSel = (f.disabilities||[]).includes(d);
                return (
                  <button key={d} onClick={() => setF(p => ({ ...p, disabilities: isSel ? p.disabilities.filter(x=>x!==d) : [...(p.disabilities||[]),d] }))}
                    style={{ padding:'3px 10px', fontSize:11, fontWeight:600, cursor:'pointer', borderRadius:12,
                      border:`1.5px solid ${isSel ? (f.color||'var(--sky)') : 'var(--border)'}`,
                      background: isSel ? `${f.color||'#1A4A6E'}15` : 'white',
                      color: isSel ? (f.color||'var(--navy)') : '#888', transition:'all .1s' }}>
                    {d}
                  </button>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:7 }}>
              <input value={disIn} onChange={e => setDisIn(e.target.value)}
                onKeyDown={e => { if(e.key==='Enter') addDis(); }}
                placeholder="Custom condition…"
                style={{ flex:1, border:'1.5px solid var(--border)', borderRadius:7, padding:'6px 9px', fontSize:12, outline:'none' }} />
              <button onClick={addDis}
                style={{ padding:'6px 13px', background:'var(--navy)', color:'white', border:'none', borderRadius:7, fontSize:11, fontWeight:700, cursor:'pointer' }}>
                Add
              </button>
            </div>
            {(f.disabilities||[]).length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:7 }}>
                {(f.disabilities||[]).map(d => (
                  <span key={d} style={{ background:'#e8f0fe', color:'var(--navy)', fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10, display:'flex', alignItems:'center', gap:4 }}>
                    {d}
                    <span onClick={() => setF(p=>({...p,disabilities:p.disabilities.filter(x=>x!==d)}))} style={{ cursor:'pointer', color:'#aaa', fontSize:13 }}>×</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label style={{ fontSize:10, fontWeight:700, color:'var(--mist)', letterSpacing:.6, textTransform:'uppercase', display:'block', marginBottom:5 }}>Clinical Notes (optional)</label>
            <textarea value={f.clinicalNotes||''} onChange={e => setF(p=>({...p,clinicalNotes:e.target.value}))}
              rows={2} placeholder="Relevant clinical or educational notes…"
              style={{ width:'100%', border:'1.5px solid var(--border)', borderRadius:8, padding:'8px 11px', fontSize:12, outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit', lineHeight:1.6 }} />
          </div>
        </div>
        <div style={{ padding:'14px 22px', borderTop:'1px solid var(--border)', display:'flex', gap:8, justifyContent:'flex-end', background:'var(--fog)' }}>
          <button onClick={onClose}
            style={{ padding:'8px 16px', background:'white', color:'#666', border:'1.5px solid var(--border)', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer' }}>
            Cancel
          </button>
          <button disabled={!f.name?.trim() || saving || (isNew && classrooms.length > 0 && !f.classroomId)}
            onClick={() => { if(f.name?.trim()) onSave(f); }}
            style={{ padding:'8px 20px',
              background: (f.name?.trim() && !(isNew && classrooms.length > 0 && !f.classroomId)) ? 'var(--navy)' : '#ccc',
              color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:12,
              cursor: (f.name?.trim() && !(isNew && classrooms.length > 0 && !f.classroomId)) ? 'pointer' : 'not-allowed', minWidth:100 }}>
            {saving ? 'Saving…' : isNew ? 'Add Child' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}